import { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Search, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { sportsApi } from '../SportsAuthContext';

// Replace player (court ⋯, owner 2026-10-01). Tap everyone who is leaving,
// pick who steps in for each (search on top; suggestions underneath, hidden
// while searching), say what happens to each leaver, confirm. Before the
// first point it is a clean swap; mid-game it is allowed but warned — the
// player who leaves still gets the result, the replacement just plays it out.
// Never a no-show.

const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return (d && (d.message || (typeof d === 'string' ? d : null))) || fallback;
};
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const waited = (m) => (m == null ? 'not played yet' : m < 60 ? `waiting ${m} min` : `waiting ${Math.floor(m / 60)} h ${m % 60} min`);

const AFTER = [
  ['queue', 'Back in queue', 'Free again — the rotation picks them up.'],
  ['pause', 'Pause', 'Sits out until you resume them (injury, rest).'],
  ['done', 'Done', 'Leaves the session.'],
];

// Suggested replacements for one leaver: free right now, not due up next,
// fewest games → skill level closest to the leaver → waited longest.
export function rankSuggestions(players, leaverLevel, taken = []) {
  const lv = Number(leaverLevel) || 0;
  return (players || [])
    .filter(p => p.status === 'free' && !taken.includes(p.id) && p.next_position !== 1)
    .sort((x, y) => (x.games - y.games)
      || (Math.abs(x.level - lv) - Math.abs(y.level - lv))
      || ((y.waited_minutes ?? -1) - (x.waited_minutes ?? -1))
      || x.name.localeCompare(y.name))
    .slice(0, 3);
}

const C = {
  surface: '#0E0708', line: '#2E161B', lineStrong: '#3A1A20', amber: '#F59E0B',
  sheet: 'linear-gradient(180deg, #170B0E 0%, #100809 100%)',
};

function Tag({ p }) {
  const look = p.status === 'on_court' ? ['#FF6B76', '#E6394618', '#E6394644', p.court_name ? `ON ${p.court_name.toUpperCase()}` : 'IN A GAME']
    : p.status === 'paused' ? ['#9A9A9A', '#FFFFFF08', '#2F2F2F', 'PAUSED']
      : p.status === 'flagged' ? ['#9A9A9A', '#FFFFFF08', '#2F2F2F', 'AWAY']
        : p.next_position ? ['#FCD34D', '#F59E0B14', '#F59E0B44', `UP NEXT #${p.next_position}`]
          : ['#4ADE80', '#22C55E14', '#22C55E44', 'FREE'];
  return (
    <span className="ml-1.5 text-[9.5px] font-extrabold rounded px-1.5 py-0.5 whitespace-nowrap align-[1px]"
      style={{ color: look[0], background: look[1], border: `1px solid ${look[2]}`, letterSpacing: '0.05em' }}>{look[3]}</span>
  );
}

function Level({ v }) {
  return (
    <span className="text-xs px-1.5 py-px rounded shrink-0 tabular-nums"
      style={{ fontFamily: 'Oswald, sans-serif', color: '#D4D4D4', background: '#0A0A0A', border: '1px solid #262626' }}>
      {v == null ? '—' : Number(v).toFixed(1)}
    </span>
  );
}

function Step({ n, done, children }) {
  return (
    <p className="flex items-center gap-2 text-[10.5px] font-extrabold uppercase mt-4 mb-2" style={{ color: '#777', letterSpacing: '0.12em' }}>
      <span className="grid place-items-center w-[18px] h-[18px] rounded-full text-[10px]"
        style={done ? { background: '#22C55E', color: '#062' } : { background: '#1E1E1E', border: '1px solid #333', color: '#ddd' }}>{n}</span>
      {children}
    </p>
  );
}

export default function ReplacePlayerModal({ sessionId, slot, courtName, requirePin, clearPin, onClose, onDone }) {
  const [opts, setOpts] = useState(null);
  const [err, setErr] = useState('');
  const [leavers, setLeavers] = useState([]);
  const [subs, setSubs] = useState({});
  const [after, setAfter] = useState({});
  const [active, setActive] = useState(null);
  const [q, setQ] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await sportsApi.get(`/sports/open-play/${sessionId}/slots/${slot.id}/replace-options`);
      setOpts(r.data);
      setErr('');
      return r.data;
    } catch (e) {
      setErr(errMsg(e, 'Could not load the players'));
      return null;
    }
  }, [sessionId, slot.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const mid = !!opts?.started;
  const players = useMemo(() => opts?.players || [], [opts]);
  const byId = useMemo(() => Object.fromEntries(players.map(p => [p.id, p])), [players]);
  const match = opts?.match || [];
  const matchById = Object.fromEntries(match.map(m => [m.id, m]));
  const taken = Object.values(subs).filter(Boolean);
  const allSet = leavers.length > 0 && leavers.every(l => subs[l]);
  const defaultAfter = mid ? 'pause' : 'queue';
  const afterOf = (l) => after[l] || defaultAfter;

  const toggleLeaver = (id) => {
    if (leavers.includes(id)) {
      const rest = leavers.filter(x => x !== id);
      const s = { ...subs }; delete s[id];
      setLeavers(rest); setSubs(s);
      if (active === id) setActive(rest.find(x => !s[x]) || null);
    } else {
      const order = match.map(m => m.id).filter(x => x === id || leavers.includes(x));
      setLeavers(order);
      if (!active) setActive(id);
    }
    setQ('');
  };
  const pickSub = (leaver, pid) => {
    const s = { ...subs, [leaver]: pid };
    setSubs(s);
    setQ('');
    setActive(leavers.find(x => !s[x]) || null);
  };

  const levelAfter = (team) => match.filter(m => m.team === team)
    .reduce((t, m) => t + Number((subs[m.id] ? byId[subs[m.id]]?.level : m.level) || 0), 0);
  const levelBefore = (team) => match.filter(m => m.team === team).reduce((t, m) => t + Number(m.level || 0), 0);

  const submit = async () => {
    const pin = await requirePin({ fresh: false });
    if (pin === null) return;
    setBusy(true);
    try {
      const r = await sportsApi.put(`/sports/open-play/${sessionId}/slots/${slot.id}/replace`, {
        pin, swaps: leavers.map(l => ({ out: l, in: subs[l], after: afterOf(l) })),
      });
      const res = r.data?.results || [];
      const line = res.map(x => (x.mode === 'stand_in' ? `${x.in.name} plays for ${x.out.name}` : `${x.in.name} in for ${x.out.name}`)).join(' · ');
      if (r.data?.partial) {
        toast.warning(`${line || 'Nothing changed'} — ${r.data.error?.message || 'the rest could not be swapped'}`);
      } else {
        toast.success(line + (res.some(x => x.mode === 'stand_in') ? ' — the result goes to the player who left' : ''));
      }
      onDone();
    } catch (e) {
      if (e.response?.status === 403) clearPin();
      setErr(errMsg(e, 'Could not replace the player'));
      setConfirming(false);
      if (e.response?.status === 409) {
        setSubs({}); setLeavers([]); setActive(null);
        await load();
      }
    } finally { setBusy(false); }
  };

  const optRow = (p, rank, forWho) => {
    const disabled = p.status !== 'free';
    const lv = matchById[forWho]?.level;
    const diff = lv == null ? null : Math.abs(p.level - lv);
    const where = p.status === 'on_court' ? 'playing now'
      : p.next_position ? `next game #${p.next_position}` : waited(p.waited_minutes);
    return (
      <button key={p.id} type="button" data-testid={`replace-pick-${p.id}`} disabled={disabled}
        onClick={() => pickSub(forWho, p.id)}
        className="w-full grid items-center gap-2.5 text-left px-3 py-2.5 hover:bg-[#1A0D10] disabled:hover:bg-transparent"
        style={{ gridTemplateColumns: 'minmax(0,1fr) auto', background: C.surface, borderTop: `1px solid ${C.line}`, opacity: disabled ? 0.38 : 1 }}>
        <span className="min-w-0">
          <span className="text-sm font-bold" style={{ color: '#E5E5E5' }}>
            {rank && <span className="mr-1.5" style={{ fontFamily: 'Oswald, sans-serif', color: C.amber }}>{rank}</span>}
            {p.name}
          </span>
          <Tag p={p} />
          <span className="block text-xs mt-0.5" style={{ color: '#8A8A8A' }}>
            {plural(p.games, 'game', 'games')} · {where}{diff != null ? ` · ${diff < 0.05 ? 'same level' : `${diff.toFixed(1)} off`}` : ''}
          </span>
        </span>
        <Level v={p.level} />
      </button>
    );
  };

  const box = { background: C.surface, border: `1px solid ${C.line}` };
  const backToBack = taken.filter(id => byId[id]?.next_position && byId[id].next_position <= 2);

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:p-4" data-testid="replace-modal"
      style={{ background: 'rgba(0,0,0,0.78)' }} onClick={() => { if (!busy) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="replace-title"
        className="w-full sm:max-w-xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-4 sm:p-5"
        style={{ background: C.sheet, border: `1px solid ${C.lineStrong}`,
                 boxShadow: '0 30px 80px #000, 0 0 0 1px rgba(230,57,70,0.12), 0 0 60px -20px rgba(230,57,70,0.35)' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <h3 id="replace-title" className="text-lg font-bold uppercase" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff', letterSpacing: 0.5 }}>
            {confirming ? `Replace ${leavers.length > 1 ? plural(leavers.length, 'player', 'players') : 'player'}?` : `Replace player${courtName ? ` · ${courtName}` : ''}`}
          </h3>
          <button onClick={onClose} disabled={busy} aria-label="Close" style={{ color: '#737373' }}><X className="h-5 w-5" /></button>
        </div>

        {!opts && !err && <p className="text-sm py-6 text-center" style={{ color: '#737373' }}>Loading players…</p>}

        {opts && confirming && (
          <>
            <p className="text-sm mt-1 mb-3.5" style={{ color: '#A3A3A3' }}>
              Are you sure you want to replace {leavers.length > 1 ? 'these players' : 'this player'}{courtName ? ` on ${courtName}` : ''}?
            </p>
            <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${C.line}` }}>
              {leavers.map((l, i) => (
                <div key={l} data-testid={`replace-confirm-${l}`} className="px-3 py-2.5 text-sm" style={{ background: C.surface, borderTop: i ? `1px solid ${C.line}` : 0 }}>
                  <b style={{ color: '#fff' }}>{matchById[l]?.name}</b> <span style={{ color: '#666' }}>→</span>{' '}
                  <b style={{ color: '#4ADE80' }}>{byId[subs[l]]?.name}</b>
                  <span className="block text-xs mt-0.5" style={{ color: '#8A8A8A' }}>
                    {matchById[l]?.name}: {AFTER.find(a => a[0] === afterOf(l))[1].toLowerCase()}{mid && afterOf(l) !== 'queue' ? ' when this game ends' : ''}
                  </span>
                </div>
              ))}
            </div>
            {mid && (
              <p className="text-xs mt-3 px-3 py-2.5 rounded-lg" style={{ color: '#FDE68A', background: '#F59E0B12', border: '1px solid #F59E0B55', borderLeft: '4px solid #F59E0B' }}>
                The game is at {opts.score_a}–{opts.score_b}. {leavers.map(l => matchById[l]?.playing_for?.name || matchById[l]?.name).join(' & ')} {leavers.length > 1 ? 'get' : 'gets'} the result.
              </p>
            )}
          </>
        )}

        {opts && !confirming && (
          <>
            <p className="text-sm mt-1" style={{ color: '#A3A3A3' }}>
              {mid ? <>Score is <b style={{ color: '#fff' }}>{opts.score_a}–{opts.score_b}</b>.</> : 'Nobody has scored yet — a clean swap.'}{' '}
              Tap <b style={{ color: '#fff' }}>everyone</b> who is leaving, then pick who steps in for each.
            </p>
            {mid && (
              <p data-testid="replace-midgame-warning" className="text-[12.5px] mt-3 px-3 py-2.5 rounded-lg leading-relaxed"
                style={{ color: '#FDE68A', background: '#F59E0B12', border: '1px solid #F59E0B55', borderLeft: '4px solid #F59E0B' }}>
                <b style={{ color: '#FCD34D' }}>Swapping mid-game isn't recommended — but you can.</b> The score stays {opts.score_a}–{opts.score_b}.
                Each <b style={{ color: '#FCD34D' }}>player who leaves gets this game's result</b> (win or lose); the replacement just plays it out and their own game count doesn't change.
              </p>
            )}

            <Step n={1} done={leavers.length > 0}>Who is leaving? <span className="normal-case tracking-normal font-semibold" style={{ color: '#666' }}>— tap one or more</span></Step>
            {/* Left vs right like the court card: Team A red on the left, Team B blue on the right */}
            <div className="grid items-stretch gap-2" style={{ gridTemplateColumns: 'minmax(0,1fr) auto minmax(0,1fr)' }}>
              {['a', 'b'].map((t, ti) => {
                const red = t === 'a';
                const col = red ? { name: '#FF6B76', line: '#E6394666', tint: '#E639460F', head: '#FF6B76' }
                  : { name: '#6EA8FF', line: '#3B82F666', tint: '#3B82F60F', head: '#6EA8FF' };
                const column = (
                  <div key={t} className="flex flex-col gap-2 min-w-0" data-testid={`replace-team-${t}`}>
                    <p className={`text-[11px] font-extrabold uppercase ${red ? '' : 'text-right'}`}
                      style={{ fontFamily: 'Oswald, sans-serif', color: col.head, letterSpacing: '0.08em' }}>Team {t.toUpperCase()}</p>
                    {match.filter(m => m.team === t).map(m => {
                      const on = leavers.includes(m.id);
                      return (
                        <button key={m.id} type="button" data-testid={`replace-leaver-${m.id}`} aria-pressed={on} onClick={() => toggleLeaver(m.id)}
                          className={`rounded-xl px-3 py-2.5 ${red ? 'text-left' : 'text-right'}`}
                          style={{ background: on ? '#F59E0B14' : col.tint, border: `1.5px solid ${on ? C.amber : col.line}`,
                                   boxShadow: on ? `0 0 0 1px ${C.amber} inset` : 'none' }}>
                          <b className="block text-sm truncate" style={{ color: col.name }}>{on ? '✓ ' : ''}{m.name}</b>
                          <span className="block text-[11.5px]" style={{ color: '#9A9A9A' }}>
                            level {m.level == null ? '—' : Number(m.level).toFixed(1)}
                            {m.playing_for ? ` · for ${m.playing_for.name}` : ''}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                );
                return ti === 0 ? column : [
                  <div key="vs" className="flex items-center justify-center pt-5">
                    <span className="text-[11px] font-black px-2 py-1 rounded-md"
                      style={{ fontFamily: 'Oswald, sans-serif', color: '#D4D4D4', background: '#0A0A0A', border: '1px solid #333', letterSpacing: '0.08em' }}>VS</span>
                  </div>,
                  column,
                ];
              })}
            </div>
            {leavers.length >= 3 && !mid && (
              <p className="text-xs mt-3 px-3 py-2.5 rounded-lg" style={{ color: '#FDE68A', background: '#F59E0B12', border: '1px solid #F59E0B55', borderLeft: '4px solid #F59E0B' }}>
                {leavers.length} of {match.length} are leaving. It may be simpler to <b>Void game</b> — nothing is recorded and the next match takes the court. Replacing still works if you prefer.
              </p>
            )}

            {leavers.length > 0 && (
              <>
                <Step n={2} done={allSet}>Who steps in?</Step>
                <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${C.line}` }}>
                  {leavers.map((l, i) => {
                    const s = subs[l]; const on = active === l;
                    return (
                      <button key={l} type="button" data-testid={`replace-slot-${l}`} onClick={() => { setActive(on ? null : l); setQ(''); }}
                        className="w-full flex items-center justify-between gap-2 text-left px-3 py-2.5"
                        style={{ background: on ? '#F59E0B12' : C.surface, boxShadow: on ? `inset 3px 0 0 ${C.amber}` : 'none', borderTop: i ? `1px solid ${C.line}` : 0 }}>
                        <span className="text-sm">
                          <b style={{ color: '#fff' }}>{matchById[l]?.name}</b> <ArrowRight className="inline h-3.5 w-3.5 mx-0.5" style={{ color: '#666' }} />{' '}
                          {s ? <><b style={{ color: '#4ADE80' }}>{byId[s]?.name}</b> <Level v={byId[s]?.level} /></>
                            : <span className="text-[13px] font-bold" style={{ color: on ? '#FCD34D' : '#777' }}>{on ? 'picking…' : 'tap to pick'}</span>}
                        </span>
                        {s && <span className="text-xs font-bold" style={{ color: '#777' }}>Change</span>}
                      </button>
                    );
                  })}
                </div>
                {active && (
                  <div className="mt-3">
                    <div className="relative mb-2.5">
                      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[18px] w-[18px] pointer-events-none" style={{ color: C.amber }} />
                      <input data-testid="replace-search" value={q} onChange={e => setQ(e.target.value)} autoComplete="off"
                        placeholder={`Search any player for ${matchById[active]?.name || ''}…`}
                        className="w-full rounded-xl pl-11 pr-3.5 py-3 text-[15px] font-semibold outline-none placeholder:text-[#B5B5B5] placeholder:font-medium"
                        style={{ minHeight: 48, background: '#1C1013', color: '#fff', border: '1.5px solid #F59E0B70',
                                 boxShadow: '0 0 0 4px #F59E0B10, 0 6px 18px -8px rgba(245,158,11,0.45)' }}
                        onFocus={e => { e.target.style.borderColor = C.amber; e.target.style.boxShadow = '0 0 0 4px #F59E0B2E, 0 8px 22px -8px rgba(245,158,11,0.6)'; }}
                        onBlur={e => { e.target.style.borderColor = '#F59E0B70'; e.target.style.boxShadow = '0 0 0 4px #F59E0B10, 0 6px 18px -8px rgba(245,158,11,0.45)'; }} />
                    </div>
                    {q.trim() ? (() => {
                      const hits = players.filter(p => p.name.toLowerCase().includes(q.trim().toLowerCase())
                        && (!taken.includes(p.id) || subs[active] === p.id));
                      return (
                        <div data-testid="replace-results" className="rounded-xl overflow-hidden" style={{ border: `1px solid ${C.line}` }}>
                          {hits.length ? hits.map(p => optRow(p, null, active))
                            : <p className="px-3 py-2.5 text-sm" style={{ background: C.surface, color: '#777' }}>No player matches “{q.trim()}”</p>}
                        </div>
                      );
                    })() : (() => {
                      const top = rankSuggestions(players, matchById[active]?.level, taken.filter(t => t !== subs[active]));
                      return (
                        <>
                          <p className="text-[10.5px] font-extrabold uppercase mt-1 mb-2" style={{ color: '#777', letterSpacing: '0.12em' }}>Suggested for {matchById[active]?.name}</p>
                          <div data-testid="replace-suggested" className="rounded-xl overflow-hidden" style={{ border: `1px solid ${C.line}` }}>
                            {top.length ? top.map((p, i) => optRow(p, i + 1, active))
                              : <p className="px-3 py-2.5 text-sm" style={{ background: C.surface, color: '#777' }}>Nobody is free right now — search above to pick someone.</p>}
                          </div>
                          <p className="text-[11.5px] mt-2 leading-relaxed" style={{ color: '#777' }}>
                            Fewest games tonight → skill level closest to {matchById[active]?.name} → waited longest. Never someone on court or due up next. Type above to pick anyone else.
                          </p>
                        </>
                      );
                    })()}
                  </div>
                )}
              </>
            )}

            {allSet && (
              <>
                <Step n={3} done>What happens to {leavers.length > 1 ? 'them' : matchById[leavers[0]]?.name}?</Step>
                {leavers.map(l => (
                  <div key={l} className="mb-2">
                    {leavers.length > 1 && <p className="text-[12.5px] font-extrabold mb-1.5 ml-0.5" style={{ color: '#fff' }}>{matchById[l]?.name}</p>}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {AFTER.map(([k, t, hint]) => {
                        const on = afterOf(l) === k;
                        return (
                          <button key={k} type="button" data-testid={`replace-after-${l}-${k}`} onClick={() => setAfter({ ...after, [l]: k })}
                            className="text-left rounded-xl px-3 py-2.5"
                            style={{ ...box, borderColor: on ? C.amber : C.line, background: on ? '#F59E0B0D' : C.surface, boxShadow: on ? `inset 0 0 0 1px ${C.amber}` : 'none' }}>
                            <b className="block text-[13.5px]" style={{ color: '#fff' }}>{t}</b>
                            <span className="text-xs" style={{ color: '#8A8A8A' }}>{hint}{mid && k !== 'queue' ? ' After this game.' : ''}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {(() => {
                  const A1 = levelAfter('a'); const B1 = levelAfter('b'); const d = Math.abs(A1 - B1);
                  return (
                    <div className="flex flex-wrap items-center gap-2.5 rounded-xl px-3 py-2.5 text-[12.5px] mt-3" style={{ ...box, color: '#BDBDBD' }}>
                      Teams after the swap <b style={{ color: '#fff', fontFamily: 'Oswald, sans-serif', fontSize: 15 }}>{A1.toFixed(1)}</b> vs{' '}
                      <b style={{ color: '#fff', fontFamily: 'Oswald, sans-serif', fontSize: 15 }}>{B1.toFixed(1)}</b>
                      <span style={{ color: '#666' }}>(was {levelBefore('a').toFixed(1)} vs {levelBefore('b').toFixed(1)})</span>
                      <span className="ml-auto font-extrabold" style={{ color: d <= 0.5 ? '#4ADE80' : d <= 1 ? '#A3E635' : '#FCD34D' }}>{d <= 0.5 ? 'Even' : d <= 1 ? 'Close' : 'Uneven'}</span>
                    </div>
                  );
                })()}
                {backToBack.length > 0 && (
                  <p className="text-xs mt-3 px-3 py-2.5 rounded-lg" style={{ color: '#FDE68A', background: '#F59E0B12', border: '1px solid #F59E0B55', borderLeft: '4px solid #F59E0B' }}>
                    {backToBack.map(id => `${byId[id].name} is also in match #${byId[id].next_position}`).join('; ')} — two games in a row. Their seat there stays.
                  </p>
                )}
              </>
            )}
          </>
        )}

        {err && <p data-testid="replace-error" className="text-xs mt-3 px-3 py-2 rounded-lg" style={{ color: '#FCA5A5', background: '#EF44441A', border: '1px solid #EF444440' }}>{err}</p>}

        <div className="flex gap-2 justify-end sticky bottom-0 pt-3 mt-3" style={{ background: '#100809' }}>
          <button type="button" data-testid="replace-cancel" onClick={() => (confirming ? setConfirming(false) : onClose())} disabled={busy}
            className="flex-1 sm:flex-none px-4 py-2.5 rounded-lg text-sm font-bold" style={{ color: '#E5E5E5', border: `1px solid ${C.lineStrong}`, background: '#120A0C' }}>
            {confirming ? 'Back' : 'Cancel'}
          </button>
          <button type="button" data-testid={confirming ? 'replace-yes' : 'replace-go'} disabled={busy || !allSet}
            onClick={() => (confirming ? submit() : setConfirming(true))}
            className="flex-1 sm:flex-none px-4 py-2.5 rounded-lg text-sm font-black uppercase"
            style={{ background: C.amber, color: '#111', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5,
                     opacity: busy || !allSet ? 0.45 : 1, boxShadow: busy || !allSet ? 'none' : '0 6px 22px -6px rgba(245,158,11,0.75)' }}>
            {busy ? 'Replacing…' : confirming ? 'Yes, replace'
              : `${leavers.length > 1 ? `Replace ${leavers.length} players` : 'Replace player'}${mid ? ' anyway' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
